import React, { createContext, useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ClientTabs, useClientTab } from '../hooks/useClientTab';
import { useCreateClient } from '../hooks/useCreateClient';
import { Client } from 'src/admin/users/types';
import { useNavigate } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';

type Flow = 'client' | 'create-family' | 'edit-family' | null;

type ClientPrefill = {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
};

interface ClientsContextType {
  flow: Flow;
  flowId?: string;
  clientPrefill: ClientPrefill | undefined;
  isLoading: boolean;
  error: Error | null;
  startClientCreation: (prefill?: ClientPrefill) => void;
  startFamilyCreation: () => void;
  editFamily: (id: string) => void;
  createClient: (
    data: {
      emailAddress?: string | null;
      firstName: string;
      lastName: string;
      advisorUserId: string;
      familyId?: string;
    },
    navigateAfterCreation?: boolean,
  ) => Promise<void>;
  close: () => void;
}

const ClientsContext = createContext<ClientsContextType | undefined>(undefined);

export function ClientsProvider({ children }: { children: React.ReactNode }) {
  const { setTab } = useClientTab();
  const [flow, setFlow] = useState<Flow>(null);
  const [flowId, setFlowId] = useState<string>();
  const [clientPrefill, setClientPrefill] = useState<ClientPrefill>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { createClient, error: createClientError } = useCreateClient();
  const queryClient = useQueryClient();
  const { clientId } = useCurrentClientIfAvailable();
  const navigate = useNavigate();

  const startClientCreation = (prefill?: ClientPrefill) => {
    setClientPrefill(prefill);
    setFlow('client');
    setError(null);
  };

  const startFamilyCreation = () => {
    setFlow('create-family');
    setError(null);
  };

  const editFamily = (id: string) => {
    setFlowId(id);
    setFlow('edit-family');
    setError(null);
  };

  const close = () => {
    setFlow(null);
    setClientPrefill(undefined);
    setError(null);
  };

  const handleCreateClient = async (
    data: {
      emailAddress?: string | null;
      firstName: string;
      lastName: string;
      advisorUserId: string;
      familyId?: string;
    },
    navigateAfterCreation: boolean = true,
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await createClient(data);
      setFlow(null);
      if (navigateAfterCreation) {
        setTab(ClientTabs.Clients);
        await queryClient.invalidateQueries({ queryKey: ['clients'] });
        navigateAfterClientCreation(result.data);
      }
    } catch (error) {
      setError(error as Error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  function navigateAfterClientCreation(client: Client) {
    const currentPath = window.location.pathname;
    const isClientPath = clientId != null && currentPath.startsWith(`/clients/${clientId}`);
    const isAccessible = !currentPath.includes('fileId');

    if (isClientPath && isAccessible) {
      const newPath = currentPath.replace(clientId, client.id);
      navigate(newPath);
    } else {
      navigate(`/client/${client.id}/non-financial-data`);
    }
  }

  return (
    <ClientsContext
      value={{
        flow,
        flowId,
        clientPrefill,
        isLoading,
        error: error || createClientError,
        startClientCreation,
        startFamilyCreation,
        editFamily,
        createClient: handleCreateClient,
        close,
      }}
    >
      {children}
    </ClientsContext>
  );
}

export function useClientsContext() {
  const context = useContext(ClientsContext);
  if (context === undefined) {
    throw new Error('useClientsContext must be used within a ClientsProvider');
  }
  return context;
}
